import dayjs, { Dayjs } from 'dayjs'
import { Enum } from '../../common/constants'
import { OpenProject, TimesheetRow } from './timesheetsApi'

export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

export const TimesheetSortFields = {
  TimecardDate: 'timecardDate',
  CrewMember: 'crewMember',
  CostCode: 'costCode',
  Division: 'division',
  ProjectNo: 'projectNo',
  ProjectName: 'projectName',
  ProjectWorkarea: 'projectWorkarea',
  Customer: 'customer',
  CreatedBy: 'createdBy',
  Started: 'started',
  Ended: 'ended',
  TotalTime: 'totalTime',
  Exported: 'exported',
} as const
export type TimesheetSortField = Enum<typeof TimesheetSortFields>

export const DEFAULT_SORT_FIELD: TimesheetSortField = TimesheetSortFields.TimecardDate

export type TimesheetsFilters = {
  starting?: string
  ending?: string
  crewId?: number | null
  projectId?: number | null
  search?: string
  includeUnsubmitted?: boolean
  includeExported?: boolean
}

export type TimesheetsGridRequest = {
  deletedView: boolean
  page: number
  pageSize: number
  sortField: TimesheetSortField
  sortDescending: boolean
  filters: TimesheetsFilters
}

export const buildTimesheetsRequestBody = ({ page, pageSize, sortField, sortDescending, filters }: TimesheetsGridRequest): URLSearchParams => {
  const params = new URLSearchParams()
  params.set('draw', String(page))
  params.set('start', String((page - 1) * pageSize))
  params.set('length', String(pageSize))
  params.set('search[value]', filters.search ?? '')
  params.set('search[regex]', 'false')
  params.set('columns[0][data]', sortField)
  params.set('columns[0][name]', '')
  params.set('columns[0][searchable]', 'true')
  params.set('columns[0][orderable]', 'true')
  params.set('columns[0][search][value]', '')
  params.set('columns[0][search][regex]', 'false')
  params.set('order[0][column]', '0')
  params.set('order[0][dir]', sortDescending ? 'desc' : 'asc')
  return params
}

export const buildTimesheetsQueryString = (filters: TimesheetsFilters): string => {
  const params = new URLSearchParams()
  if (filters.starting) {
    params.set('starting', filters.starting)
  }
  if (filters.ending) {
    params.set('ending', filters.ending)
  }
  if (filters.crewId) {
    params.set('crewId', String(filters.crewId))
  }
  if (filters.projectId) {
    params.set('projectId', String(filters.projectId))
  }
  params.set('includeUnsubmitted', filters.includeUnsubmitted ? 'true' : 'false')
  params.set('includeExported', filters.includeExported ? 'true' : 'false')
  return `?${params.toString()}`
}

export const RowIndicators = {
  Exported: 'exported',
  Unsubmitted: 'unsubmitted',
  MissingQboLink: 'missingQboLink',
  Selectable: 'selectable',
} as const
export type RowIndicator = Enum<typeof RowIndicators>

export const rowIndicator = (row: TimesheetRow): RowIndicator => {
  if (row.exported) {
    return RowIndicators.Exported
  }
  if (row.status < 2) {
    return RowIndicators.Unsubmitted
  }
  if (row.customerQboId == null && !row.isUnbillableTime) {
    return RowIndicators.MissingQboLink
  }
  return RowIndicators.Selectable
}

export const canSelectRow = (row: TimesheetRow): boolean => rowIndicator(row) === RowIndicators.Selectable

export const selectableRowIds = (rows: TimesheetRow[]): string[] => rows.filter(canSelectRow).map(row => row.id)

export const toggleRowSelection = (
  selectedIds: string[],
  selectableIdsInOrder: string[],
  rowId: string,
  shiftKey: boolean
): string[] => {
  if (!selectableIdsInOrder.includes(rowId)) {
    return selectedIds
  }
  if (!shiftKey) {
    if (selectedIds.includes(rowId)) {
      return selectedIds.filter(id => id !== rowId)
    }
    return [...selectedIds, rowId]
  }
  if (selectedIds.includes(rowId)) {
    return selectedIds
  }
  const added: string[] = []
  for (let i = selectableIdsInOrder.indexOf(rowId); i >= 0; i--) {
    const id = selectableIdsInOrder[i]
    if (selectedIds.includes(id)) {
      break
    }
    added.push(id)
  }
  return [...selectedIds, ...added]
}

export const calculateSelectedHours = (rows: TimesheetRow[], selectedIds: string[]): number =>
  rows
    .filter(row => selectedIds.includes(row.id))
    .reduce((total, row) => total + Number(row.totalTime.toFixed(2)), 0)

export const visitIdForRow = (row: TimesheetRow): string | null =>
  row.timecardId && row.timecardId !== EMPTY_GUID ? row.timecardId : null

export const roundToMinutes = (value: string): Dayjs => {
  const parsed = dayjs(value)
  return parsed.startOf('minute').add(Math.round(parsed.second() / 60), 'minute')
}

export const formatTimecardDate = (value: string | null): string => (value ? dayjs(value).format('M/DD/YY') : '')

export const formatStarted = (value: string | null): string => (value ? roundToMinutes(value).format('M/DD/YY hh:mm A') : '')

export const formatEnded = (value: string | null): string => (value ? roundToMinutes(value).format('M/DD/YY hh:mm A') : 'Present')

export const formatExported = (value: string | null): string => (value ? dayjs(value).format('MM/DD/YY hh:mm A') : '')

export const formatHours = (value: number): string => value.toFixed(2)

export const costCodeLabel = (row: TimesheetRow): string => {
  if (!row.isUnbillableTime) {
    return row.costCode ?? ''
  }
  return row.costCode ? `Unbillable - ${row.costCode}` : 'Unbillable'
}

export const divisionLabel = (row: TimesheetRow): string => row.division || 'N/A'

export const buildTimesheetsReportQueryString = (filters: TimesheetsFilters): string => {
  const params = new URLSearchParams()
  params.set('starting', filters.starting ?? '')
  params.set('ending', filters.ending ?? '')
  params.set('crewid', filters.crewId ? String(filters.crewId) : '')
  params.set('projectid', filters.projectId ? String(filters.projectId) : '')
  params.set('search', filters.search ?? '')
  params.set('includeExported', filters.includeExported ? 'true' : 'false')
  params.set('includeUnsubmitted', filters.includeUnsubmitted ? 'true' : 'false')
  return `?${params.toString()}`
}

export const timesheetCsvReportUrl = (filters: TimesheetsFilters): string =>
  `/v2/time/dynamic/export${buildTimesheetsReportQueryString(filters)}`

export const timesheetSummaryCsvReportUrl = (filters: TimesheetsFilters): string =>
  `/v2/time/dynamic/summary/export${buildTimesheetsReportQueryString(filters)}`

export const timesheetPrintUploadUrl = (filters: TimesheetsFilters): string =>
  `/v2/time/print/upload${buildTimesheetsReportQueryString(filters)}`

export type TimesheetsGridState = {
  page: number
  pageSize: number
  sortField: TimesheetSortField
  sortDescending: boolean
}

export const nextGridState = (
  current: TimesheetsGridState,
  pagination: { current?: number; pageSize?: number },
  sorter: { columnKey?: string | number; order?: 'ascend' | 'descend' | null }
): TimesheetsGridState => {
  const sortField = sorter.order ? ((sorter.columnKey as TimesheetSortField) ?? DEFAULT_SORT_FIELD) : DEFAULT_SORT_FIELD
  const sortDescending = sorter.order === 'descend'
  const pageSize = pagination.pageSize ?? current.pageSize
  const resetPage = sortField !== current.sortField || sortDescending !== current.sortDescending || pageSize !== current.pageSize
  return {
    page: resetPage ? 1 : pagination.current ?? 1,
    pageSize,
    sortField,
    sortDescending,
  }
}

export const paginationSummary = (rangeStart: number, rangeEnd: number, filteredTotal: number, total: number): string => {
  const format = (value: number) => value.toLocaleString('en-US')
  const base = `Showing ${format(rangeStart)} to ${format(rangeEnd)} of ${format(filteredTotal)} entries`
  if (filteredTotal !== total) {
    return `${base} (filtered from ${format(total)} total entries)`
  }
  return base
}

export const RECENT_SEARCHES_LIMIT = 10

export const pushRecentSearch = (recentSearches: string[], term: string): string[] => {
  const trimmed = term.trim()
  if (!trimmed) {
    return recentSearches
  }
  return [trimmed, ...recentSearches.filter(previous => previous !== trimmed)].slice(0, RECENT_SEARCHES_LIMIT)
}

export const crewMemberOptions = (crewMembers: Record<string, string>): { value: number; label: string }[] =>
  Object.entries(crewMembers).map(([id, name]) => ({ value: Number(id), label: name }))

export const openProjectOptions = (projects: OpenProject[]): { value: number; label: string }[] =>
  projects.map(project => ({
    value: project.id,
    label: project.customer ? `${project.project ?? ''} — ${project.customer}` : project.project ?? '',
  }))
